<template>
  <div class="dashboard">
    <div class="dashboard-header mb-4">
      <h2 class="white--text pa-4 rounded" style="background: linear-gradient(135deg, #1a237e, #0d47a1);">
        ダッシュボード
      </h2>
    </div>

    <v-card class="mb-4 pa-4">
      <v-row align="center" dense>
        <v-col cols="2">
          <v-select
            v-model="filterMode"
            :items="filterModes"
            item-text="text"
            item-value="value"
            label="検索方法"
            dense
            hide-details
          />
        </v-col>
        <v-col cols="2" v-if="filterMode === 'fiscal_year'">
          <v-select
            v-model="selectedFiscalYear"
            :items="fiscalYears"
            item-text="text"
            item-value="value"
            label="年度"
            dense
            hide-details
          />
        </v-col>
        <template v-else>
          <v-col cols="2">
            <v-menu
              v-model="startMenu"
              :close-on-content-click="false"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="開始日"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="startDate" no-title @input="startMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="2">
            <v-menu
              v-model="endMenu"
              :close-on-content-click="false"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="終了日"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="endDate" no-title @input="endMenu = false" />
            </v-menu>
          </v-col>
        </template>
        <v-col cols="2">
          <v-select
            v-model="selectedSubjectId"
            :items="subjects"
            item-text="name"
            item-value="id"
            label="教科"
            clearable
            dense
            hide-details
          />
        </v-col>
        <v-col cols="2">
          <v-select
            v-model="selectedGradeId"
            :items="grades"
            item-text="name"
            item-value="id"
            label="学年"
            clearable
            dense
            hide-details
          />
        </v-col>
        <v-col cols="1">
          <v-btn color="secondary" small @click="fetchDashboard" :loading="loading">検索</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <div v-if="loading && !dashboardData" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <template v-if="dashboardData">
      <v-row class="mb-4">
        <v-col cols="4">
          <v-card class="summary-card text-center pa-4" color="blue lighten-5">
            <div class="text-h4 font-weight-bold blue--text">{{ formatNumber(dashboardData.totalTestCount) }}</div>
            <div class="subtitle-1 mt-1">テスト作成数</div>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card class="summary-card text-center pa-4" color="green lighten-5">
            <div class="text-h4 font-weight-bold green--text">{{ formatNumber(dashboardData.userCount) }}</div>
            <div class="subtitle-1 mt-1">教師ユーザー数</div>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card class="summary-card text-center pa-4" color="orange lighten-5">
            <div class="text-h4 font-weight-bold orange--text text--darken-2">{{ formatNumber(dashboardData.totalContentUsage) }}</div>
            <div class="subtitle-1 mt-1">コンテンツ使用回数</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">日別テスト作成数</div>
            <div id="chart-daily-test"></div>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">月別コンテンツ使用回数</div>
            <div id="chart-monthly-content"></div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">月別テスト作成数推移</div>
            <div id="chart-monthly-test"></div>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">曜日別テスト作成分布</div>
            <div id="chart-weekday"></div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">コンテンツ別使用回数 Top10</div>
            <div id="chart-content-ranking"></div>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">プリセット別使用回数 Top10</div>
            <div id="chart-preset-ranking"></div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">学校別テスト作成数 Top10</div>
            <div id="chart-school-ranking"></div>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card class="pa-4">
            <div class="subtitle-1 font-weight-bold mb-2">月別アカウント申し込み件数</div>
            <div id="chart-monthly-account"></div>
          </v-card>
        </v-col>
      </v-row>

      <!-- コンテンツ別使用回数ランキングテーブル -->
      <v-card class="ranking-card mb-4">
        <div class="ranking-card__header" @click="contentRankingExpanded = !contentRankingExpanded">
          <v-icon size="20" color="#f57c00" class="mr-2">mdi-trophy</v-icon>
          <span class="font-weight-bold">コンテンツ別使用回数ランキング</span>
          <v-chip v-if="contentRankingTotal" x-small color="orange" text-color="white" class="ml-2">
            {{ contentRankingTotal }}件
          </v-chip>
          <v-spacer />
          <v-icon size="20">{{ contentRankingExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-show="contentRankingExpanded">
            <v-simple-table dense class="ranking-table">
              <thead>
                <tr>
                  <th class="text-center ranking-table__rank-col">順位</th>
                  <th class="text-left">コード</th>
                  <th class="text-left">教科</th>
                  <th class="text-left">学年</th>
                  <th class="text-left">単元名</th>
                  <th class="text-right ranking-table__count-col">使用回数</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in visibleContentRanking"
                  :key="index"
                  :class="{ 'ranking-table__row--even': index % 2 === 1 }"
                >
                  <td class="text-center">
                    <div
                      v-if="index < 3"
                      class="ranking-table__medal"
                      :class="`ranking-table__medal--${['gold', 'silver', 'bronze'][index]}`"
                    >
                      <v-icon size="14" color="white">mdi-trophy</v-icon>
                      <span class="ranking-table__medal-num">{{ index + 1 }}</span>
                    </div>
                    <span v-else class="ranking-table__rank-num">{{ index + 1 }}</span>
                  </td>
                  <td>
                    <v-chip
                      x-small
                      color="primary"
                      text-color="white"
                      label
                      class="ranking-table__code-chip"
                      @click="openContentImages(item)"
                    >{{ item.code }}</v-chip>
                  </td>
                  <td>{{ item.subjectName }}</td>
                  <td>{{ item.gradeName }}</td>
                  <td class="font-weight-medium">{{ item.name }}</td>
                  <td class="text-right">
                    <div class="ranking-table__bar-wrap">
                      <div
                        class="ranking-table__bar"
                        :style="{
                          width: rankingBarWidth(item.count) + '%',
                          backgroundColor: rankingColors[index % rankingColors.length],
                        }"
                      />
                      <span class="ranking-table__bar-label">{{ item.count.toLocaleString() }}</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!contentRankingTotal">
                  <td colspan="6" class="text-center grey--text pa-8">データがありません</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="contentRankingHasMore" class="ranking-table__more">
              <v-btn text color="primary" @click="contentRankingShowCount += RANKING_PAGE_SIZE">
                <v-icon left size="18">mdi-chevron-down</v-icon>
                もっと見る（{{ contentRankingShowCount }} / {{ contentRankingTotal }}件）
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
      </v-card>

      <!-- プリセット別使用回数ランキングテーブル -->
      <v-card class="ranking-card mb-4">
        <div class="ranking-card__header" @click="presetRankingExpanded = !presetRankingExpanded">
          <v-icon size="20" color="#7e57c2" class="mr-2">mdi-trophy</v-icon>
          <span class="font-weight-bold">プリセット別使用回数ランキング</span>
          <v-chip v-if="presetRankingTotal" x-small color="deep-purple" text-color="white" class="ml-2">
            {{ presetRankingTotal }}件
          </v-chip>
          <v-spacer />
          <v-icon size="20">{{ presetRankingExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-show="presetRankingExpanded">
            <v-simple-table dense class="ranking-table">
              <thead>
                <tr>
                  <th class="text-center ranking-table__rank-col">順位</th>
                  <th class="text-left">コード</th>
                  <th class="text-left">教科</th>
                  <th class="text-left">学年</th>
                  <th class="text-left">プリセット名</th>
                  <th class="text-right ranking-table__count-col">使用回数</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in visiblePresetRanking"
                  :key="index"
                  :class="{ 'ranking-table__row--even': index % 2 === 1 }"
                >
                  <td class="text-center">
                    <div
                      v-if="index < 3"
                      class="ranking-table__medal"
                      :class="`ranking-table__medal--${['gold', 'silver', 'bronze'][index]}`"
                    >
                      <v-icon size="14" color="white">mdi-trophy</v-icon>
                      <span class="ranking-table__medal-num">{{ index + 1 }}</span>
                    </div>
                    <span v-else class="ranking-table__rank-num">{{ index + 1 }}</span>
                  </td>
                  <td>
                    <v-chip
                      x-small
                      color="deep-purple"
                      text-color="white"
                      label
                      class="ranking-table__code-chip"
                      @click="openPresetPreview(item)"
                    >{{ item.code }}</v-chip>
                  </td>
                  <td>{{ item.subjectName }}</td>
                  <td>{{ item.gradeName }}</td>
                  <td class="font-weight-medium">{{ item.name }}</td>
                  <td class="text-right">
                    <div class="ranking-table__bar-wrap">
                      <div
                        class="ranking-table__bar"
                        :style="{
                          width: presetRankingBarWidth(item.count) + '%',
                          backgroundColor: presetColors[index % presetColors.length],
                        }"
                      />
                      <span class="ranking-table__bar-label">{{ item.count.toLocaleString() }}</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!presetRankingTotal">
                  <td colspan="6" class="text-center grey--text pa-8">データがありません</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="presetRankingHasMore" class="ranking-table__more">
              <v-btn text color="deep-purple" @click="presetRankingShowCount += RANKING_PAGE_SIZE">
                <v-icon left size="18">mdi-chevron-down</v-icon>
                もっと見る（{{ presetRankingShowCount }} / {{ presetRankingTotal }}件）
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
      </v-card>
    </template>

    <!-- コンテンツ画像モーダル -->
    <v-dialog v-model="imageDialog" max-width="900" scrollable>
      <v-card v-if="imageDialogContent">
        <v-toolbar flat dense color="primary" dark>
          <v-toolbar-title class="text-subtitle-1">
            {{ imageDialogContent.code }} - {{ imageDialogContent.name }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="imageDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <div v-if="imageLoading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="40" />
        </div>
        <template v-else>
          <v-tabs v-model="imageTab" background-color="grey lighten-4" show-arrows>
            <v-tab v-for="imageType in availableImageTypes" :key="imageType.value">
              <v-icon small class="mr-1">{{ imageType.icon }}</v-icon>
              {{ imageType.text }}
              <v-chip x-small class="ml-1">{{ (contentImages[imageType.value] || []).length }}</v-chip>
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="imageTab" style="min-height: 400px;">
            <v-tab-item v-for="imageType in availableImageTypes" :key="imageType.value" eager>
              <v-card-text class="pa-4" style="max-height: 70vh; overflow-y: auto;">
                <div
                  v-for="img in (contentImages[imageType.value] || [])"
                  :key="img.id"
                  class="image-large-item mb-3"
                >
                  <div class="image-large-item__label caption grey--text mb-1">{{ img.file_name }}</div>
                  <v-img
                    :src="img.expiring_url"
                    contain
                    class="grey lighten-4 rounded image-large-item__img"
                    @click="openPreview(img)"
                  >
                    <template #placeholder>
                      <div class="d-flex align-center justify-center fill-height">
                        <v-progress-circular indeterminate size="32" color="grey" />
                      </div>
                    </template>
                  </v-img>
                </div>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
          <div v-if="!hasAnyImages" class="text-center grey--text pa-8">
            画像が登録されていません
          </div>
        </template>
      </v-card>
    </v-dialog>

    <!-- 画像プレビューモーダル -->
    <v-dialog v-model="previewDialog" max-width="900">
      <v-card v-if="previewImage">
        <v-toolbar flat dense>
          <v-toolbar-title class="text-subtitle-2">{{ previewImage.file_name }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <div class="pa-4 text-center" style="background: #f5f5f5;">
          <v-img :src="previewImage.expiring_url" max-height="80vh" contain>
            <template #placeholder>
              <div class="d-flex align-center justify-center fill-height">
                <v-progress-circular indeterminate size="40" color="secondary" />
              </div>
            </template>
          </v-img>
        </div>
      </v-card>
    </v-dialog>

    <!-- プリセットプレビューモーダル -->
    <v-dialog v-model="presetDialog" max-width="900" scrollable>
      <v-card v-if="presetDialogItem">
        <v-toolbar flat dense color="deep-purple" dark>
          <v-toolbar-title class="text-subtitle-1">
            {{ presetDialogItem.code }} - {{ presetDialogItem.name }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="presetDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <div v-if="presetLoading" class="text-center pa-8">
          <v-progress-circular indeterminate color="deep-purple" size="40" />
        </div>
        <template v-else>
          <v-tabs v-model="presetTab" background-color="grey lighten-4" show-arrows>
            <v-tab v-for="pt in availablePresetPdfTypes" :key="pt.value">
              <v-icon small class="mr-1">{{ pt.icon }}</v-icon>
              {{ pt.text }}
              <v-chip x-small class="ml-1">{{ presetPdfUrls[pt.value].length }}</v-chip>
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="presetTab" style="min-height: 400px;">
            <v-tab-item v-for="pt in availablePresetPdfTypes" :key="pt.value" eager>
              <div style="max-height: 70vh; overflow-y: auto;">
                <div v-for="(url, i) in presetPdfUrls[pt.value]" :key="i" class="pa-2">
                  <embed :src="apiBase + url" type="application/pdf" width="100%" height="600px" />
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
          <div v-if="!hasAnyPresetPdfs" class="text-center grey--text pa-8">
            PDFが生成されていません
          </div>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApexCharts from 'apexcharts'

const RANKING_PAGE_SIZE = 50
const RANKING_COLORS = [
  '#5b86c7', '#43a047', '#ffa726', '#ef5350', '#ab47bc',
  '#26c6da', '#8d6e63', '#78909c', '#ec407a', '#42a5f5',
]
const PRESET_COLORS = [
  '#7e57c2', '#5c6bc0', '#42a5f5', '#26c6da', '#66bb6a',
  '#ffa726', '#ef5350', '#8d6e63', '#78909c', '#ec407a',
]

export default {
  name: 'Dashboard',

  data() {
    return {
      loading: false,
      dashboardData: null,
      filterMode: 'fiscal_year',
      filterModes: [
        { text: '年度指定', value: 'fiscal_year' },
        { text: '期間指定', value: 'date_range' },
      ],
      selectedFiscalYear: null,
      startDate: null,
      endDate: null,
      startMenu: false,
      endMenu: false,
      selectedSubjectId: null,
      selectedGradeId: null,
      charts: {},
      RANKING_PAGE_SIZE,
      contentRankingExpanded: true,
      contentRankingShowCount: RANKING_PAGE_SIZE,
      rankingColors: RANKING_COLORS,
      presetRankingExpanded: true,
      presetRankingShowCount: RANKING_PAGE_SIZE,
      presetColors: PRESET_COLORS,
      imageDialog: false,
      imageDialogContent: null,
      imageLoading: false,
      contentImages: {},
      imageTab: 0,
      previewDialog: false,
      previewImage: null,
      imageTypes: [
        { value: 'question', text: '問題', icon: 'mdi-help-circle-outline' },
        { value: 'answer', text: '解答', icon: 'mdi-check-circle-outline' },
        { value: 'answer_sheet', text: '解答用紙', icon: 'mdi-file-document-outline' },
        { value: 'script', text: '台本', icon: 'mdi-script-text-outline' },
      ],
      presetDialog: false,
      presetDialogItem: null,
      presetLoading: false,
      presetTab: 0,
      presetPdfUrls: { question: [], answer: [], answer_sheet: [] },
      presetPdfTypes: [
        { value: 'question', text: '問題用紙', icon: 'mdi-help-circle-outline' },
        { value: 'answer', text: '解答', icon: 'mdi-check-circle-outline' },
        { value: 'answer_sheet', text: '解答用紙', icon: 'mdi-file-document-outline' },
      ],
    }
  },

  computed: {
    subjects() {
      return this.$store.getters['master/subjectBases'] || []
    },
    grades() {
      return this.$store.state.master.allGrades || []
    },
    fiscalYears() {
      const now = new Date()
      const currentFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
      const years = []
      for (let y = currentFY; y >= 2024; y--) {
        years.push({ text: `${y}年度`, value: y })
      }
      return years
    },
    rankingItems() {
      if (!this.dashboardData || !this.dashboardData.contentUsageRanking) return []
      return this.dashboardData.contentUsageRanking.map((item, index) => ({
        rank: index + 1,
        id: item.id,
        code: item.code,
        name: item.name,
        subjectName: item.subjectName,
        gradeName: item.gradeName,
        count: item.count,
      }))
    },
    visibleContentRanking() {
      return this.rankingItems.slice(0, this.contentRankingShowCount)
    },
    contentRankingTotal() {
      return this.rankingItems.length
    },
    contentRankingHasMore() {
      return this.contentRankingShowCount < this.contentRankingTotal
    },
    rankingMaxCount() {
      if (!this.rankingItems.length) return 1
      return Math.max(...this.rankingItems.map(r => r.count), 1)
    },
    presetRankingItems() {
      if (!this.dashboardData || !this.dashboardData.presetUsageRanking) return []
      return this.dashboardData.presetUsageRanking.map((item, index) => ({
        rank: index + 1,
        presetId: item.presetId,
        code: item.code,
        name: item.name,
        subjectName: item.subjectName,
        gradeName: item.gradeName,
        count: item.count,
      }))
    },
    visiblePresetRanking() {
      return this.presetRankingItems.slice(0, this.presetRankingShowCount)
    },
    presetRankingTotal() {
      return this.presetRankingItems.length
    },
    presetRankingHasMore() {
      return this.presetRankingShowCount < this.presetRankingTotal
    },
    presetRankingMaxCount() {
      if (!this.presetRankingItems.length) return 1
      return Math.max(...this.presetRankingItems.map(r => r.count), 1)
    },
    hasAnyImages() {
      return this.imageTypes.some(t => (this.contentImages[t.value] || []).length > 0)
    },
    availableImageTypes() {
      return this.imageTypes.filter(t => (this.contentImages[t.value] || []).length > 0)
    },
    apiBase() {
      return process.env.VUE_APP_API_ENDPOINT || ''
    },
    availablePresetPdfTypes() {
      return this.presetPdfTypes.filter(t => (this.presetPdfUrls[t.value] || []).length > 0)
    },
    hasAnyPresetPdfs() {
      return this.presetPdfTypes.some(t => (this.presetPdfUrls[t.value] || []).length > 0)
    },
  },

  created() {
    const now = new Date()
    this.selectedFiscalYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
  },

  async mounted() {
    await this.fetchDashboard()
  },

  beforeDestroy() {
    this.destroyAllCharts()
  },

  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return '0'
      return num.toLocaleString()
    },

    rankingBarWidth(count) {
      return Math.round((count / this.rankingMaxCount) * 100)
    },

    presetRankingBarWidth(count) {
      return Math.round((count / this.presetRankingMaxCount) * 100)
    },

    async openContentImages(item) {
      this.imageDialogContent = item
      this.contentImages = {}
      this.imageTab = 0
      this.imageDialog = true
      this.imageLoading = true
      try {
        const res = await this.$axios(`/api/v1/admin/contents/${item.id}/content_images`)
        this.contentImages = res.data
      } catch (e) {
        console.error('Content images fetch error:', e)
      } finally {
        this.imageLoading = false
      }
    },

    openPreview(img) {
      this.previewImage = img
      this.previewDialog = true
    },

    async openPresetPreview(item) {
      this.presetDialogItem = item
      this.presetPdfUrls = { question: [], answer: [], answer_sheet: [] }
      this.presetTab = 0
      this.presetDialog = true
      this.presetLoading = true
      try {
        const res = await this.$axios(`/api/v1/admin/presets/${item.presetId}`)
        const data = res.data
        this.presetPdfUrls = {
          question: data.question_sheet_urls || [],
          answer: data.answer_urls || [],
          answer_sheet: data.answer_sheet_urls || [],
        }
      } catch (e) {
        console.error('Preset preview fetch error:', e)
      } finally {
        this.presetLoading = false
      }
    },

    async fetchDashboard() {
      this.loading = true
      this.contentRankingShowCount = RANKING_PAGE_SIZE
      this.presetRankingShowCount = RANKING_PAGE_SIZE
      try {
        const params = new URLSearchParams()
        if (this.filterMode === 'fiscal_year') {
          params.append('fiscal_year', this.selectedFiscalYear)
        } else {
          if (this.startDate) params.append('start_date', this.startDate)
          if (this.endDate) params.append('end_date', this.endDate)
        }
        if (this.selectedSubjectId) params.append('subject_id', this.selectedSubjectId)
        if (this.selectedGradeId) params.append('grade_id', this.selectedGradeId)

        const res = await this.$axios(`/api/v1/admin/dashboard?${params}`)
        this.dashboardData = res.data
        this.$nextTick(() => {
          this.renderAllCharts()
        })
      } catch (e) {
        console.error('Dashboard fetch error:', e)
      } finally {
        this.loading = false
      }
    },

    destroyAllCharts() {
      Object.values(this.charts).forEach(chart => {
        if (chart) chart.destroy()
      })
      this.charts = {}
    },

    renderAllCharts() {
      this.destroyAllCharts()
      const data = this.dashboardData
      if (!data) return

      this.renderDailyTestChart(data.dailyTestCounts)
      this.renderMonthlyContentChart(data.monthlyContentUsage)
      this.renderMonthlyTestChart(data.monthlyTestCounts)
      this.renderWeekdayChart(data.weekdayTestDistribution)
      this.renderContentRankingChart(data.contentUsageRanking)
      this.renderPresetRankingChart(data.presetUsageRanking)
      this.renderSchoolRankingChart(data.schoolTestRanking)
      this.renderMonthlyAccountChart(data.monthlyAccountApplications)
    },

    createChart(elementId, options) {
      const el = document.querySelector(`#${elementId}`)
      if (!el) return null
      const chart = new ApexCharts(el, options)
      chart.render()
      return chart
    },

    renderDailyTestChart(data) {
      const categories = (data || []).map(d => {
        const parts = d.date.split('-')
        return `${parseInt(parts[1])}/${parseInt(parts[2])}`
      })
      const series = [{ name: 'テスト作成数', data: (data || []).map(d => d.count) }]

      this.charts.dailyTest = this.createChart('chart-daily-test', {
        chart: { type: 'bar', height: 300, toolbar: { show: true } },
        colors: ['#008FFB'],
        series,
        xaxis: {
          categories,
          labels: { rotate: -45, style: { fontSize: '10px' } },
        },
        yaxis: { title: { text: '作成数' } },
        dataLabels: { enabled: false },
        plotOptions: { bar: { columnWidth: '60%' } },
      })
    },

    renderMonthlyContentChart(data) {
      const categories = (data || []).map(d => {
        const month = parseInt(d.month.split('-')[1])
        return `${month}月`
      })
      const series = [{ name: 'コンテンツ使用回数', data: (data || []).map(d => d.count) }]

      this.charts.monthlyContent = this.createChart('chart-monthly-content', {
        chart: { type: 'line', height: 300, toolbar: { show: false } },
        colors: ['#00E396'],
        series,
        xaxis: { categories },
        yaxis: { title: { text: '使用回数' } },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 5 },
        dataLabels: { enabled: true },
      })
    },

    renderMonthlyTestChart(data) {
      const categories = (data || []).map(d => {
        const month = parseInt(d.month.split('-')[1])
        return `${month}月`
      })
      const series = [{ name: 'テスト作成数', data: (data || []).map(d => d.count) }]

      this.charts.monthlyTest = this.createChart('chart-monthly-test', {
        chart: { type: 'line', height: 300, toolbar: { show: false } },
        colors: ['#008FFB'],
        series,
        xaxis: { categories },
        yaxis: { title: { text: '作成数' } },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 5 },
        dataLabels: { enabled: true },
      })
    },

    renderWeekdayChart(data) {
      const categories = (data || []).map(d => d.weekday)
      const series = [{ name: 'テスト作成数', data: (data || []).map(d => d.count) }]

      this.charts.weekday = this.createChart('chart-weekday', {
        chart: { type: 'radar', height: 300, toolbar: { show: false } },
        colors: ['#775DD0'],
        series,
        xaxis: { categories },
        yaxis: { show: false },
        markers: { size: 4 },
        fill: { opacity: 0.2 },
      })
    },

    renderContentRankingChart(data) {
      const top10 = (data || []).slice(0, 10).reverse()
      const categories = top10.map(d => d.name.length > 15 ? d.name.substring(0, 15) + '...' : d.name)

      this.charts.contentRanking = this.createChart('chart-content-ranking', {
        chart: { type: 'bar', height: 350, toolbar: { show: false } },
        colors: RANKING_COLORS,
        series: [{ name: '使用回数', data: top10.map(d => d.count) }],
        plotOptions: { bar: { horizontal: true, barHeight: '60%', distributed: true } },
        xaxis: { categories },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        legend: { show: false },
        dataLabels: { enabled: true },
      })
    },

    renderPresetRankingChart(data) {
      const top10 = (data || []).slice(0, 10).reverse()
      const categories = top10.map(d => d.name.length > 15 ? d.name.substring(0, 15) + '...' : d.name)

      this.charts.presetRanking = this.createChart('chart-preset-ranking', {
        chart: { type: 'bar', height: 350, toolbar: { show: false } },
        colors: PRESET_COLORS,
        series: [{ name: '使用回数', data: top10.map(d => d.count) }],
        plotOptions: { bar: { horizontal: true, barHeight: '60%', distributed: true } },
        xaxis: { categories },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        legend: { show: false },
        dataLabels: { enabled: true },
      })
    },

    renderMonthlyAccountChart(data) {
      const categories = (data || []).map(d => {
        const month = parseInt(d.month.split('-')[1])
        return `${month}月`
      })
      const series = [{ name: '申し込み件数', data: (data || []).map(d => d.count) }]

      this.charts.monthlyAccount = this.createChart('chart-monthly-account', {
        chart: { type: 'bar', height: 350, toolbar: { show: false } },
        colors: ['#26a69a'],
        series,
        xaxis: { categories },
        yaxis: { title: { text: '件数' } },
        plotOptions: { bar: { columnWidth: '50%' } },
        dataLabels: { enabled: true },
      })
    },

    renderSchoolRankingChart(data) {
      const top10 = (data || []).slice(0, 10).reverse()
      const categories = top10.map(d => d.name)

      this.charts.schoolRanking = this.createChart('chart-school-ranking', {
        chart: { type: 'bar', height: 350, toolbar: { show: false } },
        colors: ['#FF4560'],
        series: [{ name: 'テスト作成数', data: top10.map(d => d.count) }],
        plotOptions: { bar: { horizontal: true, barHeight: '60%' } },
        xaxis: { categories },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        dataLabels: { enabled: true },
      })
    },
  },
}
</script>

<style lang="stylus" scoped>
.dashboard
  max-width 1400px
  margin 0 auto

.summary-card
  transition transform 0.2s
  &:hover
    transform translateY(-2px)

// ランキングカード
.ranking-card
  border 1px solid #e8eaed
  border-radius 8px
  overflow hidden

  &__header
    display flex
    align-items center
    padding 14px 20px
    font-size 0.9rem
    color #37474f
    border-bottom 1px solid #f0f0f0
    background #fafbfc
    cursor pointer
    user-select none
    transition background-color 0.15s ease
    &:hover
      background #f0f2f5

// ランキングテーブル
.ranking-table
  th
    background #f5f6fa !important
    font-weight 700 !important
    font-size 0.75rem !important
    color #546e7a !important
    letter-spacing 0.06em
    border-bottom 2px solid #e0e3ea !important

  &__rank-col
    width 72px

  &__count-col
    min-width 220px
    width 220px

  &__row--even
    background-color rgba(68, 118, 170, 0.03)

  tbody tr
    transition background-color 0.15s ease
    &:hover
      background-color rgba(68, 118, 170, 0.07) !important

  // メダル
  &__medal
    display inline-flex
    align-items center
    gap 2px
    padding 3px 8px
    border-radius 20px
    font-weight 800
    font-size 0.75rem
    color #fff

    &--gold
      background linear-gradient(135deg, #ffd54f, #f9a825)
      box-shadow 0 2px 6px rgba(249, 168, 37, 0.35)

    &--silver
      background linear-gradient(135deg, #cfd8dc, #90a4ae)
      box-shadow 0 2px 6px rgba(144, 164, 174, 0.35)

    &--bronze
      background linear-gradient(135deg, #bcaaa4, #8d6e63)
      box-shadow 0 2px 6px rgba(141, 110, 99, 0.35)

  &__medal-num
    font-size 0.7rem
    line-height 1

  &__rank-num
    display inline-flex
    align-items center
    justify-content center
    width 26px
    height 26px
    border-radius 50%
    background #f0f1f5
    color #78909c
    font-weight 700
    font-size 0.8rem

  // インラインバー
  &__bar-wrap
    display flex
    align-items center
    gap 8px

  &__bar
    height 18px
    border-radius 4px
    flex-shrink 0
    transition width 0.6s ease
    min-width 4px
    max-width 150px

  &__bar-label
    font-weight 800
    font-size 0.85rem
    color #37474f
    white-space nowrap

  &__code-chip
    cursor pointer
    &:hover
      opacity 0.8

  &__more
    display flex
    justify-content center
    padding 12px 0 16px
    border-top 1px solid #f0f0f0

// 画像表示
.image-large-item
  &__label
    font-size 0.75rem

  &__img
    cursor pointer
    border 1px solid #e0e0e0
    transition box-shadow 0.2s
    &:hover
      box-shadow 0 2px 8px rgba(0, 0, 0, 0.15)
</style>
